(() => {
  'use strict';
  if (customElements.get('black-friday-preheat')) return;

  // Require an explicit UTC offset; never interpret a campaign time in a visitor's timezone.
  function parseStart(value) {
    const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(Z|[+-]\d{2}:\d{2})$/.exec(value.trim());
    if (!match) return NaN;
    const [, year, month, day, hour, minute, seconds = '00', offset] = match;
    const date = new Date(Date.UTC(+year, +month - 1, +day, +hour, +minute, +seconds));
    if (date.getUTCFullYear() !== +year || date.getUTCMonth() !== +month - 1 || date.getUTCDate() !== +day || +hour > 23 || +minute > 59 || +seconds > 59) return NaN;
    if (offset !== 'Z' && (+offset.slice(1, 3) > 14 || +offset.slice(4) > 59 || (+offset.slice(1, 3) === 14 && +offset.slice(4) !== 0))) return NaN;
    return Date.parse(value.trim());
  }

  class BlackFridayPreheat extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.cleanupPrizeMarquee();
      clearInterval(this.timer);
      this.abort = new AbortController();
      this.startAt = parseStart(this.dataset.startAt || '');
      this.navigating = false;
      const renderedTime = Number(this.dataset.serverNow) * 1000;
      this.clockTime = renderedTime > 0 ? renderedTime : NaN;
      this.clockAt = performance.now();
      this.lastSync = -Infinity;
      this.syncing = false;
      const options = { signal: this.abort.signal };
      this.addEventListener('click', (event) => this.onClick(event), options);
      this.querySelectorAll('dialog').forEach((dialog) => {
        dialog.addEventListener('click', (event) => {
          if (event.target !== dialog) return;
          const box = dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) dialog.close();
        }, options);
      });
      const resume = () => { if (!document.hidden) { this.tick(); this.syncClock(); } };
      document.addEventListener('visibilitychange', resume, options);
      window.addEventListener('pageshow', resume, options);
      this.tick();
      if (Number.isFinite(this.startAt)) {
        this.timer = setInterval(() => this.tick(), 1000);
        this.syncClock();
      }
      this.setupPrizeMarquee();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.cleanupPrizeMarquee();
      clearInterval(this.timer);
      this.querySelectorAll('dialog[open]').forEach((dialog) => dialog.close());
    }

    cleanupPrizeMarquee() {
      this.prizeResizeObserver?.disconnect();
      cancelAnimationFrame(this.prizeFrame);
      this.querySelectorAll('[data-bf-marquee-copy]').forEach((copy) => copy.remove());
      const viewport = this.querySelector('.bf-preheat__prize-marquee');
      if (viewport) {
        delete viewport.dataset.bfMarqueeActive;
        delete viewport.dataset.bfMarqueePaused;
      }
    }

    setupPrizeMarquee() {
      const options = { signal: this.abort.signal };
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.prizeViewport = this.querySelector('.bf-preheat__prize-marquee');
      this.prizeTrack = this.querySelector('.bf-preheat__prize-track');
      this.prizeList = this.querySelector('.bf-preheat__prizes');
      const refreshPrizes = () => {
        cancelAnimationFrame(this.prizeFrame);
        this.prizeFrame = requestAnimationFrame(() => this.refreshPrizes());
      };
      if (this.prizeViewport && this.prizeList) {
        if (typeof ResizeObserver !== 'undefined') {
          this.prizeResizeObserver = new ResizeObserver(refreshPrizes);
          this.prizeResizeObserver.observe(this.prizeViewport);
          this.prizeResizeObserver.observe(this.prizeList);
        } else {
          window.addEventListener('resize', refreshPrizes, options);
        }
        refreshPrizes();
      }
      this.reducedMotion.addEventListener('change', refreshPrizes, options);
      document.addEventListener('visibilitychange', () => this.updatePrizePause(), options);

    }

    refreshPrizes() {
      if (!this.isConnected || !this.prizeViewport || !this.prizeTrack || !this.prizeList) return;
      this.querySelectorAll('[data-bf-marquee-copy]').forEach((copy) => copy.remove());
      delete this.prizeViewport.dataset.bfMarqueeActive;
      if (this.reducedMotion.matches || !this.prizeList.children.length || !this.prizeViewport.clientWidth) return;
      const distance = this.prizeList.getBoundingClientRect().width + parseFloat(getComputedStyle(this.prizeTrack).columnGap);
      if (!(distance > 0)) return;
      // Fill the viewport plus one full repeat, including stores with only one prize.
      const repeats = Math.ceil(this.prizeViewport.clientWidth / distance) + 1;
      for (let index = 1; index < repeats; index += 1) {
        const copy = this.prizeList.cloneNode(true);
        copy.dataset.bfMarqueeCopy = '';
        copy.setAttribute('aria-hidden', 'true');
        copy.inert = true;
        [copy, ...copy.querySelectorAll('*')].forEach((element) => {
          element.removeAttribute('id');
          element.removeAttribute('tabindex');
          element.removeAttribute('data-shopify-editor-block');
        });
        this.prizeTrack.append(copy);
      }
      this.prizeTrack.querySelectorAll('img').forEach((image) => { image.loading = 'eager'; });
      this.prizeTrack.style.setProperty('--bf-marquee-distance', `${distance}px`);
      this.prizeTrack.style.setProperty('--bf-marquee-duration', `${distance / 32}s`);
      this.prizeViewport.scrollLeft = 0;
      this.prizeViewport.dataset.bfMarqueeActive = '';
      this.updatePrizePause();
    }

    updatePrizePause() {
      if (!this.prizeViewport) return;
      this.prizeViewport.toggleAttribute('data-bf-marquee-paused', document.hidden);
    }

    async syncClock() {
      if (!Number.isFinite(this.startAt) || this.syncing || performance.now() - this.lastSync < 30000) return;
      this.syncing = true;
      const signal = this.abort.signal;
      try {
        // HTTP Date refreshes the clock even if the Liquid HTML was cached.
        // Anchor on receipt without adding latency, so a slow request cannot start the event early.
        const response = await fetch(window.location.href, { method: 'HEAD', cache: 'no-store', signal });
        const serverTime = Date.parse(response.headers.get('Date') || '');
        if (!signal.aborted && response.ok && Number.isFinite(serverTime)) {
          this.clockTime = serverTime;
          this.clockAt = performance.now();
          this.lastSync = this.clockAt;
          this.tick();
        }
      } catch (error) {
        // Keep the server-rendered clock when offline; never fall back to a visitor's device time.
        if (error.name !== 'AbortError') console.warn('Black Friday server clock refresh unavailable.', error);
      } finally {
        if (this.abort.signal === signal) this.syncing = false;
      }
    }

    tick() {
      if (!Number.isFinite(this.startAt) || !Number.isFinite(this.clockTime)) return;
      const now = this.clockTime + Math.max(0, performance.now() - this.clockAt);
      const left = Math.max(0, Math.ceil((this.startAt - now) / 1000));
      const parts = [Math.floor(left / 86400), Math.floor(left / 3600) % 24, Math.floor(left / 60) % 60, left % 60];
      this.querySelectorAll('[data-bf-time]').forEach((element) => {
        element.textContent = String(parts[Number(element.dataset.bfTime)]).padStart(2, '0');
      });
      if (left || this.navigating || this.dataset.autoSwitch !== 'true' || this.dataset.designMode === 'true' || window.Shopify?.designMode) return;
      const raw = (this.dataset.liveUrl || '').trim();
      if (!raw) return;
      let target;
      try { target = new URL(raw, window.location.origin); } catch (_) { return; }
      // Only a different page on this store can be a live destination.
      if (!['http:', 'https:'].includes(target.protocol) || target.origin !== location.origin || target.pathname.replace(/\/+$/, '') === location.pathname.replace(/\/+$/, '')) return;
      this.navigating = true;
      window.location.replace(target.href);
    }

    onClick(event) {
      const close = event.target.closest('[data-bf-close]');
      if (close) { close.closest('dialog').close(); return; }
      const trigger = event.target.closest('[data-bf-dialog]');
      if (trigger) {
        const dialog = document.getElementById(trigger.dataset.bfDialog);
        if (dialog && this.contains(dialog) && !dialog.open) dialog.showModal();
        return;
      }
      const formButton = event.target.closest('[data-bf-form]');
      if (formButton) {
        const error = this.querySelector('.bf-preheat__form-error');
        try {
          window._klOnsite = window._klOnsite || [];
          window._klOnsite.push(['openForm', formButton.dataset.bfForm]);
          if (error) error.hidden = true;
        } catch (failure) {
          if (error) error.hidden = false;
          console.error('Black Friday form could not be opened.', failure);
        }
      }
    }
  }

  customElements.define('black-friday-preheat', BlackFridayPreheat);
})();
